/**
 * The home pill (1.J deliverable 19), a `header.pills` registration of order 40: where the page
 * lives, and an in-place disclosure (a layer, not a menu) to open the home, change it or remove it.
 * Not shown on a reference page.
 */
import type { LinkV1, SlotComponentProps } from '@codex/contracts';
import { Button, useLayer, useReturnFocus } from '@codex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useNotify } from '../../shell/notices.js';
import { useOpenDoc } from '../../shell/open-doc.js';
import { useLinksApi } from '../api.js';
import { reportFailure } from '../failure.js';
import { useLinksLive, usePageSeen } from '../live.js';
import { DocPicker } from '../picker/doc-picker.js';
import { listKey, useDocMeta, useTitles } from '../queries.js';
import { homeWrite, pillText, removeHomeWrite } from './home-model.js';
import '../links.css';

const INCOMING = { direction: 'in', linkTypeKey: 'contains' } as const;

export function HomePill({ docId }: SlotComponentProps) {
  const api = useLinksApi();
  const notify = useNotify();
  const client = useQueryClient();
  const open = useOpenDoc();
  const panelId = useId();
  const meta = useDocMeta(docId);
  const homeId = meta.data?.homeId ?? null;
  const titles = useTitles(homeId === null ? [] : [homeId]);
  const seen = usePageSeen(docId);
  const [expanded, setExpanded] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [button, setButton] = useState<HTMLButtonElement | null>(null);
  const [changeButton, setChangeButton] = useState<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLFieldSetElement>(null);
  useLinksLive(docId, homeId === null ? [] : [homeId], seen);
  const close = () => {
    setChoosing(false);
    setExpanded(false);
  };
  useLayer(expanded && !choosing, close, 'disclosure');
  useReturnFocus(expanded, button);

  useEffect(() => {
    if (!expanded) return;
    const away = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && (panel.current?.contains(target) || button?.contains(target))) return;
      close();
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  });

  if (!meta.data || meta.data.library === 'reference') return null;
  const homeTitle =
    homeId === null ? null : (titles.data?.find((t) => t.id === homeId)?.title ?? '');

  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['links'] }),
      client.invalidateQueries({ queryKey: ['tree'] }),
      client.invalidateQueries({ queryKey: ['documents', 'meta'] }),
    ]);

  const fresh = (): Promise<LinkV1[]> =>
    client.fetchQuery({
      queryKey: listKey(docId, INCOMING),
      queryFn: () => api.list(docId, INCOMING),
      staleTime: 0,
    });

  const run = async (act: () => Promise<unknown>) => {
    try {
      await act();
      await refresh();
      close();
    } catch (error) {
      reportFailure(notify, error);
      setChoosing(false);
    }
  };

  const choose = (parentId: string) =>
    run(async () => {
      const write = homeWrite(await fresh(), parentId, docId);
      if (write.kind === 'update') await api.update(write.ownerId, write.linkId, write.body);
      else await api.create(write.ownerId, write.body);
    });

  const removeHome = () =>
    run(async () => {
      const write = removeHomeWrite(await fresh());
      if (write) await api.update(write.ownerId, write.linkId, write.body);
    });

  return (
    <div className="cx-links-home">
      <button
        ref={setButton}
        type="button"
        className="cx-pill"
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        data-testid="links.home.pill"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="cx-pill__label">{pillText(homeTitle)}</span>
      </button>
      {expanded && (
        <fieldset ref={panel} id={panelId} className="cx-links-home__panel">
          <legend className="cx-visually-hidden">Home</legend>
          {homeId !== null && (
            <Button size="sm" variant="ghost" onClick={() => open(homeId)}>
              {`Open ${homeTitle ?? ''}`}
            </Button>
          )}
          <Button
            ref={setChangeButton}
            size="sm"
            variant="ghost"
            aria-expanded={choosing}
            onClick={() => setChoosing((v) => !v)}
          >
            {homeId === null ? 'Choose a home…' : 'Change home…'}
          </Button>
          {homeId !== null && (
            <Button size="sm" variant="ghost" onClick={() => void removeHome()}>
              Remove home
            </Button>
          )}
          {choosing && (
            <DocPicker
              label="Find a home"
              selfId={docId}
              createType={null}
              capability="structural"
              trigger={changeButton}
              onClose={() => setChoosing(false)}
              onCreate={() => undefined}
              onPick={(hit) => void choose(hit.id)}
            />
          )}
        </fieldset>
      )}
    </div>
  );
}
